import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import QuickActions from "./QuickActions";

const NAV = [
  { id: "studio", to: "about", route: false },
  { id: "expertise", to: "expertise", route: false },
  { id: "projects", to: "projects", route: false },
  { id: "process", to: "process", route: false },
  { id: "contact", to: "/contact", route: true },
];

export default function Header() {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (onHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (onHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const goContact = () => {
    setOpen(false);
    navigate("/contact");
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,border-color] duration-500 ${
        scrolled || !onHome
          ? "bg-[#F7F7F9]/80 backdrop-blur-xl border-b border-[#E5E5E5]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="logo-btn"
          onClick={goHome}
          className="flex items-baseline gap-2 group"
        >
          <span className="font-display text-2xl md:text-[28px] tracking-tight leading-none">
            LA<span className="text-[#DC2626]">K</span>A
          </span>
          <span className="hidden md:inline text-[10px] font-mono tracking-[0.22em] uppercase text-[#5C5C5C] group-hover:text-[#D35400] transition-colors">
            Studio · 2010
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((item, i) =>
            item.route ? (
              <Link
                key={item.id}
                to={item.to}
                data-testid={`nav-${item.id}`}
                className={`relative text-sm font-light transition-colors ${
                  location.pathname === item.to
                    ? "text-[#D35400]"
                    : "text-[#0A0A0A] hover:text-[#D35400]"
                }`}
              >
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase mr-2 text-[#5C5C5C]">
                  0{i + 1}
                </span>
                {t.nav[item.id]}
              </Link>
            ) : (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => goSection(item.to)}
                className="relative text-sm font-light text-[#0A0A0A] hover:text-[#D35400] transition-colors"
              >
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase mr-2 text-[#5C5C5C]">
                  0{i + 1}
                </span>
                {t.nav[item.id]}
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <QuickActions variant="header" source="header" />
          <div
            data-testid="lang-toggle"
            className="font-mono text-[11px] tracking-[0.25em] uppercase flex items-center gap-1 border border-[#E5E5E5] px-3 py-1.5"
          >
            <button
              data-testid="lang-fr-btn"
              onClick={() => lang !== "fr" && toggle()}
              className={`transition-colors ${lang === "fr" ? "text-[#0A0A0A]" : "text-[#B5B5B5] hover:text-[#0A0A0A]"}`}
            >
              FR
            </button>
            <span className="text-[#CCC]">/</span>
            <button
              data-testid="lang-en-btn"
              onClick={() => lang !== "en" && toggle()}
              className={`transition-colors ${lang === "en" ? "text-[#0A0A0A]" : "text-[#B5B5B5] hover:text-[#0A0A0A]"}`}
            >
              EN
            </button>
          </div>

          <button
            data-testid="mobile-menu-btn"
            aria-label="Menu"
            onClick={() => setOpen((o) => !o)}
            className="md:hidden w-10 h-10 flex items-center justify-center border border-[#E5E5E5]"
          >
            <span className="sr-only">Menu</span>
            <div className="relative w-4 h-3 flex flex-col justify-between">
              <span
                className={`block h-px bg-[#0A0A0A] transition-transform ${open ? "translate-y-[5px] rotate-45" : ""}`}
              />
              <span className={`block h-px bg-[#0A0A0A] transition-opacity ${open ? "opacity-0" : ""}`} />
              <span
                className={`block h-px bg-[#0A0A0A] transition-transform ${open ? "-translate-y-[6px] -rotate-45" : ""}`}
              />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden bg-[#F7F7F9] transition-[max-height,opacity] duration-500 ${
          open ? "max-h-[500px] opacity-100 border-t border-[#E5E5E5]" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-4">
          {NAV.map((item, i) => (
            <button
              key={item.id}
              data-testid={`mobile-nav-${item.id}`}
              onClick={() => (item.route ? goContact() : goSection(item.to))}
              className="flex items-baseline gap-3 text-left"
            >
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C]">
                0{i + 1}
              </span>
              <span className="font-display text-2xl">{t.nav[item.id]}</span>
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}
